import React from 'react';
import { css, keyframes } from '@emotion/react';
import * as NavigationMenu from '../../primitives/navigation-menu';
import Container from '../../primitives/grid/container';
import { colors } from '../../primitives/colors';
import Link from '../../primitives/link';
import Logo from '../../primitives/logo';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import { useCtaProvider } from '../../../contexts/franchise/cta-provider';
import usePrimaryNav from '../use-primary-nav';
import ColSet from '../../primitives/grid/col-set';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import { NATIONAL_CALL_CENTER_DISPLAY, PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from '../../../constants';
import formatPhoneLink from '../../utils/format-phone-link';
import MenuItem from './menu-item';
import StyledDropdown from './styled-dropdown';
import StyledPhoneBlock from './styled-phone-block';
import { atTabletBreakpoint } from './utils';
import { MenuNavItem } from '../types';
import { useNavigationContext } from '../../../contexts/navigation-context';

const fadeIn = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});

const fadeOut = keyframes({
    from: { opacity: 1 },
    to: { opacity: 0 },
});

const styles = {
    root: css({
        position: 'relative',
        zIndex: 1,
        width: '100%',
        backgroundColor: colors.shadesWhite,
    }),
    container: css({
        backgroundColor: colors.shadesWhite,
    }),
    navList: css({
        display: 'flex',
        listStyleType: 'none',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        gap: '20px',
        ...atTabletBreakpoint({
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: '0px 0px',
            gridTemplateAreas: '"logo cta""menu menu"',
            paddingTop: '8px',
        }),
    }),
    logoNavItem: css({
        position: 'relative',
        zIndex: 10,
        ...atTabletBreakpoint({
            gridArea: 'logo',
            display: 'flex',
            justifyContent: 'flex-start',
        }),
    }),
    logoContainer: css({
        display: 'flex',
        padding: '11px 0',
        justifyContent: 'flex-start',
        height: '100%',
        transition: '0.2s',
        width: '190px',
        willChange: 'width',
        '.top-bar &': {
            width: '250px', // Accounts for larger scale
        },
        '.top-bar .scrolled &': {
            width: '190px',
        },
        ...atTabletBreakpoint({
            padding: '4px 0',
        }),
    }),
    logoLink: css({
        alignSelf: 'center',
        position: 'relative',
    }),
    logoSvg: css({
        width: '184px',
        transition: '0.2s',
        transformOrigin: 'bottom left',
        willChange: 'transform',
        '.top-bar &': {
            transform: 'scale(1.3) translateY(-8px)',
        },
        '.top-bar .scrolled &': {
            transform: 'scale(1)',
        },
    }),
    menuItemsContainer: css({
        display: 'flex',
        listStyleType: 'none',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        gap: '24px',
        width: '100%',
        height: '54px',
        marginTop: 'auto',
        willChange: 'height',
        transition: '0.2s',
        '.top-bar &': {
            height: '66px',
            ...atTabletBreakpoint({
                height: '56px',
            }),
        },
        '.top-bar .scrolled &': {
            height: '54px',
        },
        ...atTabletBreakpoint({
            gridArea: 'menu',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            height: 'unset',
            marginTop: 'unset',
        }),
    }),
    endBarNavItem: css({
        position: 'relative',
        zIndex: 10,
        width: '100%',
        ...atTabletBreakpoint({
            gridArea: 'cta',
            display: 'flex',
            justifyContent: 'flex-end',
        }),
    }),
    endBarContainer: css({
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        maxWidth: '100%',
        height: '100%',
        padding: '14px 0',
        ...atTabletBreakpoint({
            padding: '0',
        }),
    }),
    endBarColSet: css({
        alignItems: 'center',
        justifyContent: 'flex-end',
    }),
    endBarHrContainer: css({
        width: '8px',
        height: '46px',
    }),
    endBarHr: css({
        height: '46px',
        display: 'block',
        backgroundColor: colors.grey200,
        width: '1px',
    }),
    navIndicator: css({
        top: 'calc(100% - 3px)', // Overlap with the bottom grey border
        zIndex: 10,
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'center',
        transition: '300ms ease',
        '&[data-state="unset"]': {
            opacity: 0,
            transform: 'translateX(250px)', // Start it at roughly logo offset
        },
        '&[data-state="hidden"]': {
            animation: `${fadeOut} 200ms ease forwards`,
        },
        '&[data-state="visible"]': {
            animation: `${fadeIn} 200ms ease forwards`,
        },
    }),
    navIndicatorItem: css({
        position: 'relative',
        backgroundColor: colors.primaryOrange,
        height: '4px',
        width: '100%',
        borderTopLeftRadius: '3px',
        borderTopRightRadius: '3px',
    }),
    viewportOuterContainer: css({
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
    }),
    viewportInnerContainer: css({
        perspective: '2000px',
        position: 'absolute',
        // left: '0',
        top: '100%',
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end',
        maxWidth: '1313px',
    }),
    viewport: css({
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 200px)',
        WebkitOverflowScrolling: 'touch',
        boxShadow: '0 4px 4px 0px #00000040',
        backgroundColor: colors.shadesWhite,
        top: '0px',
        position: 'relative',
        height: 'var(--menu-viewport-height)',
        // width: '100%',
        width: 'var(--menu-viewport-width)',
        maxWidth: '1313px',
        transformOrigin: 'top center',
        overflowX: 'hidden',
        // Reserve space for scroll bar to prevent layout shifts
        scrollbarGutter: 'stable',
        transitionProperty: 'height', // This previously had width - but too jumpy when going to 1 col from 4
        transitionDuration: '0.5s',
        transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
        '&[data-state="open"]': {
            animation: `${fadeIn} 0.3s ease forwards`,
            borderTop: `1px solid ${colors.primaryOrange}`,
        },
        '&[data-state="closed"]': {
            animation: `${fadeOut} 0.3s ease forwards`,
            borderTop: `1px solid ${colors.shadesWhite}`,
        },
        '&[data-menu-mode="self"]': {
            transform: 'translateX(var(--menu-viewport-self-x))',
        },
        '&[data-menu-mode="center"]': {
            transform: 'translateX(var(--menu-viewport-center-x))',
        },
        ...atTabletBreakpoint({
            maxHeight: 'calc(100vh - 236px)',
            '&[data-menu-mode="self"]': {
                transform: 'translateX(0)',
                width: '100%',
            },
        }),
    }),
};

const DesktopHeader = (): JSX.Element => {
    const { navItems: primaryNavLinks } = usePrimaryNav(); // These need to be typed
    const { registerDesktopNavClose } = useNavigationContext();

    const {
        isLocalCityPage,
        isMicrosite,
        permalink: micrositePermalink,
        franchise: micrositeFranchise,
        menu: micrositeMenu,
    } = useMicrosite();

    const {
        hideNationalCallCenter,
        hideFranchiseLink,
        overridePhoneNumber,
        overridePhoneNumberDescriptor,
    } = useCtaProvider();

    const { franchise: locatorFranchise, isNationalCallCenter } = useLocator();
    const franchise = micrositeFranchise || locatorFranchise?.data;

    const menuToUse: MenuNavItem[] = (isMicrosite && micrositeMenu) ? micrositeMenu.navItems : primaryNavLinks;

    // Register desktop navigation close function
    React.useEffect(() => {
        const closeDesktopNav = (): void => {
            // Call the existing JavaScript hideMenu function if it exists
            // The navigation-menu script exposes this globally when mounted
            const allMenuItems = document.querySelectorAll('[data-menu="item"]');
            allMenuItems.forEach((item) => {
                if (item instanceof HTMLElement) {
                    const trigger = item.querySelector('[data-menu="trigger"]');
                    const content = item.querySelector('[data-menu="content"]');
                    if (trigger instanceof HTMLElement && content && trigger.dataset.state === 'open') {
                        // Simulate the hideMenu functionality from the navigation script
                        const event = new Event('mouseleave');
                        const parentNav = item.closest('[data-menu-root-scope-id]');
                        if (parentNav) {
                            parentNav.dispatchEvent(event);
                        }
                    }
                }
            });
        };

        registerDesktopNavClose(closeDesktopNav);
    }, [registerDesktopNavClose]);

    return (
        <NavigationMenu.Root css={styles.root}>
            <Container css={styles.container}>
                <NavigationMenu.List css={styles.navList}>
                    <NavigationMenu.Item css={styles.logoNavItem}>
                        <div css={styles.logoContainer}>
                            <Link
                                to={(isMicrosite && micrositePermalink) ? micrositePermalink : '/'}
                                css={styles.logoLink}
                                aria-label="SERVPRO"
                            >
                                <Logo css={styles.logoSvg} />
                            </Link>
                        </div>
                    </NavigationMenu.Item>
                    <div css={styles.menuItemsContainer}>
                        {menuToUse && menuToUse.map((item) => {
                            const { navItemLink, columns = [] } = item;
                            if (!navItemLink) return null;
                            const { href } = navItemLink;

                            if (!href && columns.filter(x => x.group.filter(x => !!x).length > 0).length === 0) return null;

                            return (
                                <NavigationMenu.Item key={item.navItemLink.title}>
                                    <MenuItem item={item} />
                                </NavigationMenu.Item>
                            );
                        })}
                    </div>
                    <NavigationMenu.Item css={styles.endBarNavItem}>
                        <div css={styles.endBarContainer}>
                            <ColSet
                                breakpoints={{ dt: { between: 12 } }}
                                css={styles.endBarColSet}
                            >
                                <div>
                                    {!isMicrosite
                                        && !hideFranchiseLink
                                        && !isNationalCallCenter
                                        && (locatorFranchise?.isLoading || !locatorFranchise) && (
                                        <StyledDropdown
                                            text="Finding your Local SERVPRO"
                                        />
                                    )}
                                    {(
                                        franchise
                                        && ((!hideFranchiseLink && !isMicrosite && !isNationalCallCenter)
                                            || (isLocalCityPage && !hideFranchiseLink))
                                    ) && (
                                        <StyledDropdown
                                            text="Your Local SERVPRO"
                                            phoneNumber={franchise.mainPhone}
                                            phoneDisplay={franchise.mainPhone}
                                        />
                                    )}
                                </div>
                                <div css={styles.endBarHrContainer}>
                                    <div>
                                        {((!hideFranchiseLink
                                                && !hideNationalCallCenter
                                                && !isMicrosite
                                                && !isNationalCallCenter)
                                            || (isLocalCityPage && !hideNationalCallCenter && !hideFranchiseLink)
                                        ) && (
                                            <hr
                                                aria-orientation="vertical"
                                                css={styles.endBarHr}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    {(overridePhoneNumber && overridePhoneNumberDescriptor) && (
                                        <StyledPhoneBlock
                                            text={overridePhoneNumberDescriptor}
                                            phoneDisplay={overridePhoneNumber}
                                            phoneNumber={overridePhoneNumber}
                                        />
                                    )}
                                    {((!hideNationalCallCenter && !isMicrosite)
                                        || (isLocalCityPage && !hideNationalCallCenter)
                                    ) && (
                                        <StyledPhoneBlock
                                            text={NATIONAL_CALL_CENTER_DISPLAY}
                                            phoneDisplay={PHONE_NUMBER_DISPLAY}
                                            phoneNumber={PHONE_NUMBER}
                                        />
                                    )}
                                    {(isMicrosite && franchise) && (
                                        <StyledPhoneBlock
                                            text="Call Today - 24/7 Emergency Services"
                                            phoneDisplay={franchise?.tempOutOfService ? PHONE_NUMBER_DISPLAY : formatPhoneLink(franchise.mainPhone)}
                                            phoneNumber={franchise?.tempOutOfService ? PHONE_NUMBER : franchise.mainPhone}
                                        />
                                    )}
                                </div>
                            </ColSet>
                        </div>
                    </NavigationMenu.Item>
                    <NavigationMenu.Indicator css={styles.navIndicator}>
                        <div css={styles.navIndicatorItem} />
                    </NavigationMenu.Indicator>
                </NavigationMenu.List>
            </Container>
            <div css={styles.viewportOuterContainer}>
                <div css={styles.viewportInnerContainer}>
                    <NavigationMenu.Viewport css={styles.viewport} />
                </div>
            </div>
        </NavigationMenu.Root>
    );
};

export default DesktopHeader;
